"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"
import {
  ArrowLeft, ArrowRight, Bot, Check, CheckCircle2, Code2, Copy, Download, Globe, HelpCircle, History,
  Image as ImageIcon, Loader2, Lock, Mail, Monitor, MousePointerClick, Palette, Plus, Send, Share2, ShoppingCart,
  Smartphone, Sparkles, Type, X, Eye, Wand2,
} from "lucide-react"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { FONTS, PALETTES, PROJECT_TYPES, STYLES, type DesignPreferences } from "@/lib/dream/design"
import type { Locale } from "@/lib/i18n"
import { LivePreview } from "./LivePreview"
import { ProposalReport } from "./ProposalReport"
import type { DreamProposal } from "@/lib/ai/dream-proposal"

// ---------------------------------------------------------------------------------------------
// Hayalini Anlat studio: a configurator-style workspace. Left: the current step's fields.
// Centre: a live, code-drawn preview of the product (and the AI visual once analysed).
// Right: actions for the finished vision (quote, share, e-mail, download). Bottom: navigation.
// ---------------------------------------------------------------------------------------------

type Result = {
  story: string
  suggestedServices: string[]
  keyFeatures: string[]
  estimatedComplexity: "low" | "medium" | "high" | "very-high"
  nextSteps: string[]
  estimatedTimeline?: string
  estimatedBudget?: string
  shareToken: string | null
  shareUrl: string | null
  generatedImageUrl?: string | null
  imageGenerationAvailable?: boolean
  proposal?: DreamProposal | null
}

type PastDream = { id: string; dream_description: string; created_at: string; share_token: string; generated_image_url?: string | null }

const TYPE_ICONS: Record<string, typeof Globe> = { website: Globe, "web-app": Code2, mobile: Smartphone, ecommerce: ShoppingCart, automation: Bot, desktop: Monitor }

const SERVICE_LINKS: Record<string, { tr: string; en: string; trPath: string; enPath: string }> = {
  "web-tasarim": { tr: "Web Tasarım", en: "Web Design", trPath: "/hizmetler/web-tasarim", enPath: "/en/services/web-design" },
  "web-yazilim": { tr: "Web Yazılım", en: "Web Development", trPath: "/hizmetler/web-yazilim", enPath: "/en/services/web-development" },
  "web-programlama": { tr: "Web Yazılım", en: "Web Development", trPath: "/hizmetler/web-yazilim", enPath: "/en/services/web-development" },
  "ui-ux-tasarim": { tr: "UI & UX Tasarım", en: "UI & UX Design", trPath: "/hizmetler/ui-ux-tasarim", enPath: "/en/services/ui-ux-design" },
  "mobil-uygulama": { tr: "Mobil Uygulama", en: "Mobile App", trPath: "/hizmetler/mobil-uygulama", enPath: "/en/services/mobile-app" },
  "masaustu-uygulama": { tr: "Masaüstü Uygulama", en: "Desktop App", trPath: "/hizmetler/masaustu-uygulama", enPath: "/en/services/desktop-app" },
  "e-ticaret": { tr: "E-ticaret", en: "E-commerce", trPath: "/hizmetler/e-ticaret", enPath: "/en/services/e-commerce" },
  shopify: { tr: "Shopify", en: "Shopify", trPath: "/hizmetler/shopify", enPath: "/en/services/shopify" },
  woocommerce: { tr: "WooCommerce", en: "WooCommerce", trPath: "/hizmetler/woocommerce", enPath: "/en/services/woocommerce" },
  otomasyon: { tr: "Otomasyon", en: "Automation", trPath: "/hizmetler/otomasyon", enPath: "/en/services/automation" },
  "n8n-otomasyon": { tr: "Otomasyon", en: "Automation", trPath: "/hizmetler/otomasyon", enPath: "/en/services/automation" },
  "ai-servisler": { tr: "AI Servisler", en: "AI Services", trPath: "/hizmetler/ai-servisler", enPath: "/en/services/ai-services" },
}

const C = {
  tr: {
    studio: "Hayalini Anlat",
    tag: "Proje Stüdyosu",
    steps: ["Proje", "Tasarım", "İletişim", "Analiz", "Sonuç"],
    stepOf: (n: number) => `ADIM ${n} / 4`,
    draft: "Taslak", done: "Analiz tamamlandı",
    s0: { title: "Fikrini anlat", desc: "Ne yapmak istediğini birkaç cümleyle yaz. Önizleme sen yazdıkça değişir.", type: "Proje türü", typeHint: "Seçmezsen yapay zeka karar verir.", idea: "Projeni anlat", placeholder: "Örn: Mahalledeki fırınlar için sabah siparişi alan bir mobil uygulama. Müşteriler teslim saatini seçsin, fırıncı üretimi siparişe göre planlasın…", ideas: "Fikir mi lazım? Birine tıkla:", examples: ["Kuaförüm için online randevu ve hatırlatma sistemi", "El yapımı deri ürünlerim için e-ticaret sitesi", "Ekibimin satışlarını takip ettiği bir yönetim paneli", "Müşteri sorularını yanıtlayan yapay zeka asistanı"], min: "En az 10 karakter" },
    s1: { title: "Tasarımını seç", desc: "Marka adı, stil, renk ve yazı tipi. Hepsi isteğe bağlı; seçtiklerin önizlemeye ve yapay zeka görseline yansır.", brand: "Marka adı", brandPh: "Örn. Mahalle Fırını", style: "Tasarım stili", colors: "Renk paleti", custom: "Kendi renklerim", font: "Yazı tipi", skip: "Bu adımı atlayabilirsin." },
    s2: { title: "Seni tanıyalım", desc: "Analiz sonucunu ve proje görselini sana özel hazırlayabilmemiz için.", name: "Ad", surname: "Soyad", phone: "Telefon", email: "E-posta", sendCode: "Kod gönder", resend: "Tekrar gönder", code: "6 haneli kod", verify: "Doğrula", verified: "E-posta doğrulandı", past: "Geçmiş hayallerim", noPast: "Bu e-postayla kayıtlı hayal yok.", load: "Bu açıklamayı kullan", privacy: "Bilgilerin yalnızca projen için kullanılır. Günde 2 analiz hakkın var." },
    s3: { title: "Son kontrol", desc: "Her şey hazır. Analiz başlayınca yapay zeka projeni yorumlar ve bir konsept görseli tasarlar (~1 dk).", project: "Proje", design: "Tasarım", contact: "İletişim", edit: "Düzenle", none: "Seçilmedi (yapay zeka belirleyecek)" },
    s4: { title: "Proje vizyonun hazır", desc: "Görseli sağdaki araçlarla paylaşabilir, e-postana gönderebilir ya da teklif alabilirsin.", story: "Proje hikâyesi", services: "Önerilen hizmetler", features: "Öne çıkan özellikler", next: "Sonraki adımlar" },
    progress: ["Fikrini ve hedef kitleni inceliyorum", "Ürün stratejisini kuruyorum", "Özellikleri ve yol haritasını planlıyorum", "Süre ve bütçeyi hesaplıyorum", "Konsept görselini tasarlıyorum"],
    progressNote: "Görsel üretimi bir dakika kadar sürebilir, sayfadan ayrılma.",
    back: "Geri", next: "İleri", analyze: "Analiz Et", quote: "Teklif Al", quoteSent: "Teklif talebin alındı",
    complexity: { low: "Düşük", medium: "Orta", high: "Yüksek", "very-high": "Çok yüksek" } as Record<string, string>,
    sumComplexity: "Karmaşıklık", sumTime: "Süre", sumBudget: "Bütçe", sumHint: "Seçimlerin önizlemeye anında yansır",
    canvas: { report: "Rapor", preview: "Canlı önizleme", ai: "AI görseli", desktop: "Masaüstü", mobile: "Mobil", aiWaiting: "Görsel hazırlanıyor…" },
    rail: { quote: "Teklif al", share: "Paylaş", mail: "E-postama gönder", download: "Görseli indir", fresh: "Yeni proje", help: "Nasıl çalışır?" },
    share: { copy: "Linki kopyala", copied: "Kopyalandı", title: "Proje vizyonum" },
    mailSent: (to: string) => `Gönderildi: ${to}`,
    intro: { title: "Hayalini 4 adımda anlat", items: [["Fikrini yaz", "Önizleme sen yazdıkça şekillenir."], ["Tasarımını seç", "Renk, yazı tipi ve stil anında uygulanır."], ["Analizini al", "Yapay zeka projeni yorumlar, görselini çizer."]], ok: "ANLADIM" },
    errors: { desc: "Lütfen en az 10 karakterlik bir açıklama yaz.", contact: "Ad, soyad, telefon ve doğrulanmış e-posta gerekli.", generic: "Bir hata oluştu. Lütfen tekrar deneyin." },
  },
  en: {
    studio: "Tell Your Dream",
    tag: "Project Studio",
    steps: ["Project", "Design", "Contact", "Analysis", "Result"],
    stepOf: (n: number) => `STEP ${n} / 4`,
    draft: "Draft", done: "Analysis complete",
    s0: { title: "Describe your idea", desc: "Write a few sentences about what you want to build. The preview changes as you type.", type: "Project type", typeHint: "Leave it and the AI decides.", idea: "Your project", placeholder: "e.g. A mobile app for local bakeries to take morning pre-orders. Customers pick a delivery time, the baker plans production from the orders…", ideas: "Need an idea? Click one:", examples: ["Online booking and reminders for my hair salon", "An online store for my handmade leather goods", "A dashboard where my team tracks its sales", "An AI assistant that answers customer questions"], min: "At least 10 characters" },
    s1: { title: "Choose your design", desc: "Brand name, style, colours and typography. All optional; your choices shape the preview and the AI visual.", brand: "Brand name", brandPh: "e.g. Corner Bakery", style: "Design style", colors: "Colour palette", custom: "My own colours", font: "Typography", skip: "You can skip this step." },
    s2: { title: "Let's get to know you", desc: "So we can tailor the analysis and the project visual to you.", name: "First name", surname: "Last name", phone: "Phone", email: "Email", sendCode: "Send code", resend: "Resend", code: "6-digit code", verify: "Verify", verified: "Email verified", past: "My past dreams", noPast: "No dreams saved with this email.", load: "Use this description", privacy: "Your details are only used for your project. You have 2 analyses per day." },
    s3: { title: "Final check", desc: "All set. When the analysis starts, the AI interprets your project and designs a concept visual (~1 min).", project: "Project", design: "Design", contact: "Contact", edit: "Edit", none: "Not chosen (the AI will decide)" },
    s4: { title: "Your project vision is ready", desc: "Use the tools on the right to share it, email it to yourself or get a quote.", story: "Project story", services: "Suggested services", features: "Key features", next: "Next steps" },
    progress: ["Studying your idea and audience", "Shaping the product strategy", "Planning features and roadmap", "Estimating timeline and budget", "Designing the concept visual"],
    progressNote: "Generating the visual can take about a minute, please stay on the page.",
    back: "Back", next: "Next", analyze: "Analyse", quote: "Get a Quote", quoteSent: "Quote request received",
    complexity: { low: "Low", medium: "Medium", high: "High", "very-high": "Very high" } as Record<string, string>,
    sumComplexity: "Complexity", sumTime: "Timeline", sumBudget: "Budget", sumHint: "Your choices update the preview instantly",
    canvas: { report: "Report", preview: "Live preview", ai: "AI visual", desktop: "Desktop", mobile: "Mobile", aiWaiting: "Preparing the visual…" },
    rail: { quote: "Get a quote", share: "Share", mail: "Email it to me", download: "Download visual", fresh: "New project", help: "How it works" },
    share: { copy: "Copy link", copied: "Copied", title: "My project vision" },
    mailSent: (to: string) => `Sent to ${to}`,
    intro: { title: "Tell your dream in 4 steps", items: [["Write your idea", "The preview takes shape as you type."], ["Pick your design", "Colours, fonts and style apply instantly."], ["Get your analysis", "The AI interprets your project and draws it."]], ok: "GOT IT" },
    errors: { desc: "Please write a description of at least 10 characters.", contact: "First name, last name, phone and a verified email are required.", generic: "Something went wrong. Please try again." },
  },
}

const INTRO_KEY = "lakverse-dream-studio-intro"
const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())

function Section({ n, title, hint, children }: { n: number; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="border-b border-slate-200 px-5 py-6 last:border-b-0">
      <div className="mb-3 flex items-start gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-slate-900 font-mono text-xs font-bold text-white">{n}</span>
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
        </div>
      </div>
      <div className="pl-0 sm:pl-9">{children}</div>
    </section>
  )
}

const chip = (active: boolean) =>
  `border px-3 py-2 text-left text-sm transition-colors ${active ? "border-[#034AA6] bg-[#034AA6]/5 text-[#034AA6] ring-1 ring-[#034AA6]" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`

const input = "h-11 w-full border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-[#034AA6] focus:ring-2 focus:ring-[#034AA6]/15"

export function DreamStudio({ locale }: { locale: Locale }) {
  const t = C[locale]
  const { executeRecaptcha } = useGoogleReCaptcha()
  const panelRef = useRef<HTMLDivElement>(null)

  const [step, setStep] = useState(0)
  const [description, setDescription] = useState("")
  const [design, setDesign] = useState<DesignPreferences>({})
  const [customColors, setCustomColors] = useState(["#034AA6", "#F59E0B", "#FFFFFF"])
  const [name, setName] = useState("")
  const [surname, setSurname] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")

  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState("")
  const [verified, setVerified] = useState(false)
  const [busy, setBusy] = useState<"" | "send" | "verify">("")
  const [verifyMsg, setVerifyMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [pastDreams, setPastDreams] = useState<PastDream[] | null>(null)

  const [analyzing, setAnalyzing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState("")
  const [result, setResult] = useState<Result | null>(null)

  const [device, setDevice] = useState<"desktop" | "mobile">("desktop")
  const [deviceTouched, setDeviceTouched] = useState(false)
  const [canvas, setCanvas] = useState<"report" | "preview" | "ai">("preview")
  const [intro, setIntro] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [mailState, setMailState] = useState<{ busy?: boolean; text?: string; ok?: boolean }>({})
  const [quoteState, setQuoteState] = useState<"" | "busy" | "sent" | "error">("")

  // First visit: short how-it-works card
  useEffect(() => {
    try { if (!localStorage.getItem(INTRO_KEY)) setIntro(true) } catch { /* storage blocked */ }
  }, [])
  const closeIntro = () => {
    setIntro(false)
    try { localStorage.setItem(INTRO_KEY, "1") } catch { /* storage blocked */ }
  }

  // Mobile projects preview on a phone, unless the visitor picked a device themselves
  useEffect(() => {
    if (!deviceTouched) setDevice(design.projectType === "mobile" ? "mobile" : "desktop")
  }, [design.projectType, deviceTouched])

  // A changed email needs a fresh verification
  useEffect(() => {
    setVerified(false); setCodeSent(false); setCode(""); setVerifyMsg(null); setPastDreams(null)
  }, [email])

  // Analysis progress narration (the request itself takes ~30-60s because of the image)
  useEffect(() => {
    if (!analyzing) return
    setProgress(0)
    const id = setInterval(() => setProgress((p) => Math.min(p + 1, t.progress.length - 1)), 8000)
    return () => clearInterval(id)
  }, [analyzing, t.progress.length])

  const goTo = (s: number) => {
    setStep(s)
    setError("")
    panelRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const step0Valid = description.trim().length >= 10 && description.length <= 5000
  const contactValid = !!name.trim() && !!surname.trim() && phone.replace(/\D/g, "").length >= 10 && emailOk(email) && verified
  const unlocked = [true, step0Valid, step0Valid, step0Valid && contactValid, !!result]

  // ---- e-mail verification ----
  const sendCode = async () => {
    if (!emailOk(email)) return setVerifyMsg({ ok: false, text: locale === "tr" ? "Geçerli bir e-posta girin." : "Enter a valid email." })
    setBusy("send"); setVerifyMsg(null)
    try {
      const res = await fetch("/api/ai/send-verification-code", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, locale }) })
      const data = await res.json()
      if (res.ok && data.success) { setCodeSent(true); setVerifyMsg({ ok: true, text: data.message }) }
      else setVerifyMsg({ ok: false, text: data.error || t.errors.generic })
    } catch { setVerifyMsg({ ok: false, text: t.errors.generic }) }
    finally { setBusy("") }
  }

  const verifyCode = async () => {
    if (!/^\d{6}$/.test(code)) return
    setBusy("verify"); setVerifyMsg(null)
    try {
      const res = await fetch("/api/ai/verify-code", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, code, locale }) })
      const data = await res.json()
      if (res.ok && data.verified) { setVerified(true); setCode(""); setVerifyMsg(null) }
      else setVerifyMsg({ ok: false, text: data.error || t.errors.generic })
    } catch { setVerifyMsg({ ok: false, text: t.errors.generic }) }
    finally { setBusy("") }
  }

  const loadPastDreams = async () => {
    try {
      const res = await fetch(`/api/ai/get-past-dreams?email=${encodeURIComponent(email)}&locale=${locale}`)
      const data = await res.json()
      setPastDreams(data.success ? data.dreams ?? [] : [])
    } catch { setPastDreams([]) }
  }

  // ---- analysis ----
  const pollImage = useCallback((token: string) => {
    let attempts = 0
    const poll = async () => {
      if (++attempts > 40) return
      try {
        const res = await fetch(`/api/ai/get-vision-image?token=${token}`)
        const data = res.ok ? await res.json() : {}
        if (data.generatedImageUrl) return setResult((r) => (r ? { ...r, generatedImageUrl: data.generatedImageUrl } : r))
      } catch { /* keep polling */ }
      setTimeout(poll, 1500)
    }
    setTimeout(poll, 2000)
  }, [])

  const analyze = async () => {
    if (!step0Valid) return setError(t.errors.desc)
    if (!contactValid) return setError(t.errors.contact)
    setError(""); setAnalyzing(true)
    try {
      const recaptchaToken = await executeRecaptcha("dream_analysis")
      const res = await fetch("/api/ai/dream-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dream_description: description.trim(),
          user_name: `${name.trim()} ${surname.trim()}`,
          user_email: email.trim(),
          user_phone: phone.trim(),
          design,
          locale,
          recaptchaToken,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        if (data.requiresVerification) { setVerified(false); setStep(2) }
        return setError(data.error || t.errors.generic)
      }
      setResult({ ...data, shareToken: data.shareToken ?? null, shareUrl: data.shareUrl ?? null })
      setStep(4)
      setCanvas(data.proposal ? "report" : data.generatedImageUrl || data.imageGenerationAvailable ? "ai" : "preview")
      if (data.imageGenerationAvailable && !data.generatedImageUrl && data.shareToken) pollImage(data.shareToken)
    } catch {
      setError(t.errors.generic)
    } finally {
      setAnalyzing(false)
    }
  }

  // ---- result actions ----
  const requestQuote = async () => {
    if (!result?.shareToken) return
    setQuoteState("busy")
    try {
      const res = await fetch("/api/ai/request-quote", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ shareToken: result.shareToken, locale }) })
      setQuoteState(res.ok ? "sent" : "error")
    } catch { setQuoteState("error") }
  }

  const emailResult = async () => {
    if (!result?.shareToken) return
    setMailState({ busy: true })
    try {
      const res = await fetch("/api/ai/email-result", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ shareToken: result.shareToken, locale }) })
      const data = await res.json().catch(() => ({}))
      setMailState(res.ok ? { ok: true, text: t.mailSent(data.sentTo) } : { ok: false, text: data.error || t.errors.generic })
    } catch { setMailState({ ok: false, text: t.errors.generic }) }
  }

  const copyLink = async () => {
    if (!result?.shareUrl) return
    try { await navigator.clipboard.writeText(result.shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch { /* ignore */ }
  }

  const shareTo = (platform: "whatsapp" | "linkedin" | "x" | "facebook" | "telegram") => {
    if (!result?.shareUrl) return
    const url = encodeURIComponent(result.shareUrl)
    const text = encodeURIComponent(`${t.share.title}: ${description.slice(0, 90)}…`)
    const targets = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      x: `https://x.com/intent/tweet?url=${url}&text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    }
    window.open(targets[platform], "_blank", "noopener,noreferrer,width=640,height=520")
  }

  const reset = () => {
    setResult(null); setStep(0); setDescription(""); setDesign({}); setCanvas("preview")
    setQuoteState(""); setMailState({}); setShareOpen(false); setError("")
  }

  // ---- render helpers ----
  const paletteId = PALETTES.find((p) => JSON.stringify(p.colors) === JSON.stringify(design.colors))?.id
  const customActive = !!design.colors?.length && !paletteId
  const toggle = (key: "projectType" | "style" | "font", id: string) => setDesign((d) => ({ ...d, [key]: d[key] === id ? undefined : id }))
  const label = (list: { id: string; tr: string; en: string }[], id?: string) => list.find((o) => o.id === id)?.[locale]

  const primary = (() => {
    if (step === 4) {
      return quoteState === "sent"
        ? { text: t.quoteSent, icon: CheckCircle2, onClick: undefined, disabled: true }
        : { text: t.quote, icon: Send, onClick: requestQuote, disabled: quoteState === "busy" || !result?.shareToken }
    }
    if (step === 3) return { text: t.analyze, icon: Sparkles, onClick: analyze, disabled: analyzing }
    return { text: t.next, icon: ArrowRight, onClick: () => (unlocked[step + 1] ? goTo(step + 1) : setError(step === 0 ? t.errors.desc : t.errors.contact)), disabled: false }
  })()

  const railButtons = [
    { key: "quote", icon: Send, label: quoteState === "sent" ? t.quoteSent : t.rail.quote, onClick: requestQuote, disabled: !result?.shareToken || quoteState === "busy" || quoteState === "sent", done: quoteState === "sent" },
    { key: "share", icon: Share2, label: t.rail.share, onClick: () => setShareOpen((o) => !o), disabled: !result?.shareUrl },
    { key: "mail", icon: Mail, label: t.rail.mail, onClick: emailResult, disabled: !result?.shareToken || mailState.busy || mailState.ok, done: mailState.ok },
    { key: "download", icon: Download, label: t.rail.download, href: result?.generatedImageUrl || undefined, disabled: !result?.generatedImageUrl },
    { key: "fresh", icon: Plus, label: t.rail.fresh, onClick: reset, disabled: !result },
    { key: "help", icon: HelpCircle, label: t.rail.help, onClick: () => setIntro(true), disabled: false },
  ]

  return (
    // The fixed site header is 101px (tagline strip + nav) but <main> only pads 64px: add the rest.
    // Desktop: the studio fills the viewport below it and only the left panel scrolls (html/body set
    // overflow, so position: sticky does not work on this site). Mobile: bottom bar is fixed.
    <div className="mt-[37px] flex flex-col bg-slate-100 pb-[73px] font-sans lg:h-[calc(100vh-101px)] lg:pb-0">
      {/* ---------- Top bar: title + stepper ---------- */}
      <header className="z-30 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-slate-200 bg-white px-4 py-3 lg:flex-nowrap">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center bg-[#034AA6] text-white"><Wand2 className="h-4 w-4" /></span>
          <div className="leading-tight">
            <p className="font-mono text-sm font-bold text-slate-900">{t.studio}</p>
            <p className="text-[11px] text-slate-500">{t.tag}</p>
          </div>
        </div>
        <ol className="order-3 flex w-full items-center gap-1 overflow-x-auto lg:order-none lg:w-auto lg:flex-1 lg:justify-center">
          {t.steps.map((s, i) => {
            const active = step === i
            const open = unlocked[i] && !analyzing
            return (
              <li key={s} className="flex shrink-0 items-center">
                {i > 0 && <span className="mx-1.5 h-px w-5 bg-slate-300 sm:w-8" />}
                <button
                  type="button"
                  disabled={!open}
                  onClick={() => goTo(i)}
                  className={`flex items-center gap-2 px-2 py-1 text-sm transition ${active ? "font-semibold text-slate-900" : open ? "text-slate-600 hover:text-slate-900" : "cursor-not-allowed text-slate-400"}`}
                >
                  <span className={`flex h-6 w-6 items-center justify-center border text-xs ${active ? "border-slate-900 bg-slate-900 text-white" : (i < step || (i === 4 && result)) ? "border-[#034AA6] bg-[#034AA6] text-white" : "border-slate-300"}`}>
                    {!open ? <Lock className="h-3 w-3" /> : i < step || (result && i < 4) ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  {s}
                </button>
              </li>
            )
          })}
        </ol>
        <span className={`ml-auto hidden text-xs sm:inline lg:ml-0 ${result ? "text-green-700" : "text-slate-500"}`}>{result ? t.done : t.draft}</span>
      </header>

      {/* ---------- Workspace ---------- */}
      <div className="grid flex-1 grid-cols-1 lg:min-h-0 lg:grid-cols-[400px_1fr_96px] lg:pr-8">
        {/* Left panel */}
        <aside ref={panelRef} className="order-1 border-r border-slate-200 bg-white lg:overflow-y-auto">
          <div className="border-b border-slate-200 px-5 py-5">
            <p className="font-mono text-[11px] font-bold tracking-widest text-slate-500">{step < 4 ? `${t.stepOf(step + 1)} · ${t.steps[step].toUpperCase()}` : t.steps[4].toUpperCase()}</p>
            <h2 className="mt-1 text-2xl font-bold text-slate-900">{[t.s0, t.s1, t.s2, t.s3, t.s4][step].title}</h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">{[t.s0, t.s1, t.s2, t.s3, t.s4][step].desc}</p>
          </div>

          {step === 0 && (
            <>
              <Section n={1} title={t.s0.type} hint={t.s0.typeHint}>
                <div className="grid grid-cols-2 gap-2">
                  {PROJECT_TYPES.map((o) => {
                    const Icon = TYPE_ICONS[o.id] ?? Globe
                    return (
                      <button key={o.id} type="button" onClick={() => toggle("projectType", o.id)} aria-pressed={design.projectType === o.id} className={`${chip(design.projectType === o.id)} flex items-center gap-2`}>
                        <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{o[locale]}</span>
                      </button>
                    )
                  })}
                </div>
              </Section>
              <Section n={2} title={t.s0.idea}>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 5000))}
                  placeholder={t.s0.placeholder}
                  rows={7}
                  className="w-full resize-y border border-slate-300 p-3 text-sm leading-relaxed outline-none transition focus:border-[#034AA6] focus:ring-2 focus:ring-[#034AA6]/15"
                />
                <div className="mt-1 flex justify-between text-xs text-slate-500">
                  <span>{description.trim().length < 10 ? t.s0.min : "✓"}</span>
                  <span>{description.length} / 5000</span>
                </div>
                <p className="mt-4 text-xs font-medium text-slate-500">{t.s0.ideas}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {t.s0.examples.map((ex) => (
                    <button key={ex} type="button" onClick={() => setDescription(ex)} className="border border-dashed border-slate-300 px-2.5 py-1.5 text-left text-xs text-slate-600 transition hover:border-[#034AA6] hover:text-[#034AA6]">
                      {ex}
                    </button>
                  ))}
                </div>
              </Section>
            </>
          )}

          {step === 1 && (
            <>
              <Section n={1} title={t.s1.brand}>
                <input className={input} value={design.brandName ?? ""} maxLength={40} placeholder={t.s1.brandPh} onChange={(e) => setDesign((d) => ({ ...d, brandName: e.target.value || undefined }))} />
              </Section>
              <Section n={2} title={t.s1.style}>
                <div className="grid grid-cols-2 gap-2">
                  {STYLES.map((o) => (
                    <button key={o.id} type="button" onClick={() => toggle("style", o.id)} aria-pressed={design.style === o.id} className={chip(design.style === o.id)}>{o[locale]}</button>
                  ))}
                </div>
              </Section>
              <Section n={3} title={t.s1.colors}>
                <div className="grid grid-cols-2 gap-2">
                  {PALETTES.map((p) => (
                    <button key={p.id} type="button" onClick={() => setDesign((d) => ({ ...d, colors: paletteId === p.id ? undefined : p.colors }))} aria-pressed={paletteId === p.id} className={`${chip(paletteId === p.id)} flex items-center gap-2`}>
                      <span className="flex h-5 w-12 shrink-0 overflow-hidden ring-1 ring-black/10">{p.colors.map((c) => <span key={c} className="flex-1" style={{ background: c }} />)}</span>
                      <span className="truncate text-xs">{p[locale]}</span>
                    </button>
                  ))}
                  <button type="button" onClick={() => setDesign((d) => ({ ...d, colors: customActive ? undefined : customColors }))} aria-pressed={customActive} className={`${chip(customActive)} col-span-2 flex items-center gap-2`}>
                    <Palette className="h-4 w-4" /> <span className="text-xs">{t.s1.custom}</span>
                  </button>
                </div>
                {customActive && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {customColors.map((c, i) => (
                      <label key={i} className="flex items-center gap-2 border border-slate-200 px-2 py-1 font-mono text-xs text-slate-600">
                        <input type="color" value={c} onChange={(e) => { const next = customColors.map((x, j) => (j === i ? e.target.value.toUpperCase() : x)); setCustomColors(next); setDesign((d) => ({ ...d, colors: next })) }} className="h-6 w-8 cursor-pointer border-0 bg-transparent p-0" />
                        {c}
                      </label>
                    ))}
                  </div>
                )}
              </Section>
              <Section n={4} title={t.s1.font} hint={t.s1.skip}>
                <div className="grid grid-cols-2 gap-2">
                  {FONTS.map((o) => (
                    <button key={o.id} type="button" onClick={() => toggle("font", o.id)} aria-pressed={design.font === o.id} className={`${chip(design.font === o.id)} flex items-center gap-2`}>
                      <span className="text-xl leading-none" style={{ fontFamily: o.css }}>Aa</span> <span className="truncate text-xs">{o[locale]}</span>
                    </button>
                  ))}
                </div>
              </Section>
            </>
          )}

          {step === 2 && (
            <>
              <Section n={1} title={`${t.s2.name} & ${t.s2.surname}`}>
                <div className="grid grid-cols-2 gap-2">
                  <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder={t.s2.name} autoComplete="given-name" />
                  <input className={input} value={surname} onChange={(e) => setSurname(e.target.value)} placeholder={t.s2.surname} autoComplete="family-name" />
                </div>
              </Section>
              <Section n={2} title={t.s2.phone}>
                <input className={input} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05XX XXX XX XX" autoComplete="tel" />
              </Section>
              <Section n={3} title={t.s2.email}>
                <div className="flex gap-2">
                  <input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@email.com" autoComplete="email" />
                  {!verified && (
                    <button type="button" onClick={sendCode} disabled={busy !== "" || !emailOk(email)} className="h-11 shrink-0 bg-slate-900 px-3 text-xs font-semibold text-white transition hover:bg-slate-700 disabled:opacity-40">
                      {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : codeSent ? t.s2.resend : t.s2.sendCode}
                    </button>
                  )}
                </div>
                {verified ? (
                  <p className="mt-3 flex items-center gap-2 text-sm font-medium text-green-700"><CheckCircle2 className="h-4 w-4" /> {t.s2.verified}</p>
                ) : codeSent ? (
                  <div className="mt-3 flex gap-2">
                    <input
                      className={`${input} text-center font-mono text-lg tracking-[0.4em]`}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="000000"
                      aria-label={t.s2.code}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      onKeyDown={(e) => e.key === "Enter" && verifyCode()}
                    />
                    <button type="button" onClick={verifyCode} disabled={busy !== "" || code.length !== 6} className="h-11 shrink-0 bg-[#034AA6] px-4 text-xs font-semibold text-white transition hover:bg-[#022859] disabled:opacity-40">
                      {busy === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : t.s2.verify}
                    </button>
                  </div>
                ) : null}
                {verifyMsg && <p className={`mt-2 text-xs ${verifyMsg.ok ? "text-[#034AA6]" : "text-red-600"}`}>{verifyMsg.text}</p>}

                {verified && (
                  <div className="mt-4">
                    <button type="button" onClick={loadPastDreams} className="flex items-center gap-2 text-sm font-medium text-[#034AA6] hover:underline">
                      <History className="h-4 w-4" /> {t.s2.past}
                    </button>
                    {pastDreams && (
                      <div className="mt-2 max-h-56 space-y-2 overflow-y-auto">
                        {pastDreams.length === 0 && <p className="text-xs text-slate-500">{t.s2.noPast}</p>}
                        {pastDreams.map((d) => (
                          <div key={d.id} className="flex gap-2 border border-slate-200 p-2">
                            {d.generated_image_url && (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={d.generated_image_url} alt="" className="h-12 w-16 shrink-0 object-cover" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="line-clamp-2 text-xs text-slate-700">{d.dream_description}</p>
                              <div className="mt-1 flex gap-3 text-[11px]">
                                <button type="button" className="text-[#034AA6] hover:underline" onClick={() => { setDescription(d.dream_description); goTo(0) }}>{t.s2.load}</button>
                                <Link href={`/${locale === "en" ? "en/" : ""}proje-vizyonu/${d.share_token}`} className="text-slate-500 hover:underline" target="_blank"><Eye className="inline h-3 w-3" /></Link>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Section>
              <p className="px-5 pb-6 text-xs text-slate-500">{t.s2.privacy}</p>
            </>
          )}

          {step === 3 && (
            <div className="space-y-3 p-5">
              {[
                { title: t.s3.project, to: 0, lines: [label(PROJECT_TYPES, design.projectType) ?? t.s3.none, description] },
                { title: t.s3.design, to: 1, lines: [design.brandName, label(STYLES, design.style), label(FONTS, design.font)].filter(Boolean) as string[], colors: design.colors },
                { title: t.s3.contact, to: 2, lines: [`${name} ${surname}`, phone, email] },
              ].map((b) => (
                <div key={b.title} className="border border-slate-200 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="font-mono text-xs font-bold tracking-widest text-slate-500">{b.title.toUpperCase()}</p>
                    <button type="button" onClick={() => goTo(b.to)} disabled={analyzing} className="text-xs font-semibold text-[#034AA6] hover:underline">{t.s3.edit}</button>
                  </div>
                  {b.lines.length ? b.lines.map((l, i) => <p key={i} className={`text-sm text-slate-700 ${i === 1 && b.to === 0 ? "line-clamp-4 text-slate-500" : ""}`}>{l}</p>) : <p className="text-sm text-slate-400">{t.s3.none}</p>}
                  {b.colors && <div className="mt-2 flex gap-1">{b.colors.map((c) => <span key={c} className="h-5 w-5 ring-1 ring-black/10" style={{ background: c }} />)}</div>}
                </div>
              ))}
            </div>
          )}

          {step === 4 && result && (
            <div className="space-y-6 p-5">
              {result.proposal ? (
                <div className="border-l-4 border-[#034AA6] pl-4">
                  <p className="text-lg font-bold leading-snug text-slate-900">{result.proposal.headline}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{result.proposal.summary}</p>
                  {canvas !== "report" && (
                    <button type="button" onClick={() => setCanvas("report")} className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-[#034AA6] hover:underline"><Sparkles className="h-4 w-4" />{locale === "tr" ? "Raporun tamamını gör" : "View the full report"}</button>
                  )}
                </div>
              ) : (
              <div>
                <p className="mb-2 font-mono text-xs font-bold tracking-widest text-slate-500">{t.s4.story.toUpperCase()}</p>
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">{result.story}</p>
              </div>
              )}
              {result.suggestedServices.length > 0 && (
                <div>
                  <p className="mb-2 font-mono text-xs font-bold tracking-widest text-slate-500">{t.s4.services.toUpperCase()}</p>
                  <div className="flex flex-wrap gap-2">
                    {result.suggestedServices.map((s) => {
                      const svc = SERVICE_LINKS[s]
                      return svc ? (
                        <Link key={s} href={locale === "en" ? svc.enPath : svc.trPath} target="_blank" className="border border-[#034AA6]/30 bg-[#034AA6]/5 px-2.5 py-1 text-xs font-medium text-[#034AA6] hover:bg-[#034AA6]/10">{svc[locale]}</Link>
                      ) : <span key={s} className="border border-slate-200 px-2.5 py-1 text-xs">{s}</span>
                    })}
                  </div>
                </div>
              )}
              {!result.proposal && result.keyFeatures.length > 0 && (
                <div>
                  <p className="mb-2 font-mono text-xs font-bold tracking-widest text-slate-500">{t.s4.features.toUpperCase()}</p>
                  <ul className="space-y-1.5">{result.keyFeatures.map((f) => <li key={f} className="flex gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#034AA6]" />{f}</li>)}</ul>
                </div>
              )}
              {result.nextSteps.length > 0 && (
                <div>
                  <p className="mb-2 font-mono text-xs font-bold tracking-widest text-slate-500">{t.s4.next.toUpperCase()}</p>
                  <ol className="space-y-1.5">{result.nextSteps.map((s, i) => <li key={s} className="flex gap-2 text-sm text-slate-700"><span className="flex h-5 w-5 shrink-0 items-center justify-center bg-slate-900 font-mono text-[10px] text-white">{i + 1}</span>{s}</li>)}</ol>
                </div>
              )}
            </div>
          )}

          {error && <p className="mx-5 mb-5 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </aside>

        {/* Canvas */}
        <main className="relative order-2 flex min-h-[460px] flex-col overflow-hidden bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:18px_18px]">
          <div className="flex items-center justify-between gap-2 p-3">
            <div className="flex bg-white shadow-sm ring-1 ring-slate-200">
              {result?.proposal && (
                <button type="button" onClick={() => setCanvas("report")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${canvas === "report" ? "bg-slate-900 text-white" : "text-slate-600"}`}><Sparkles className="h-3.5 w-3.5" />{t.canvas.report}</button>
              )}
              <button type="button" onClick={() => setCanvas("preview")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${canvas === "preview" ? "bg-slate-900 text-white" : "text-slate-600"}`}><MousePointerClick className="h-3.5 w-3.5" />{t.canvas.preview}</button>
              {result && (
                <button type="button" onClick={() => setCanvas("ai")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${canvas === "ai" ? "bg-slate-900 text-white" : "text-slate-600"}`}><ImageIcon className="h-3.5 w-3.5" />{t.canvas.ai}</button>
              )}
            </div>
            {canvas === "preview" && (
              <div className="flex bg-white shadow-sm ring-1 ring-slate-200">
                {(["desktop", "mobile"] as const).map((d) => (
                  <button key={d} type="button" onClick={() => { setDevice(d); setDeviceTouched(true) }} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${device === d ? "bg-slate-900 text-white" : "text-slate-600"}`}>
                    {d === "desktop" ? <Monitor className="h-3.5 w-3.5" /> : <Smartphone className="h-3.5 w-3.5" />}{t.canvas[d]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={`flex flex-1 justify-center overflow-auto p-4 sm:p-8 ${canvas === "report" ? "items-start" : "items-center"}`}>
            {canvas === "report" && result?.proposal ? (
              <ProposalReport
                proposal={result.proposal}
                imageUrl={result.generatedImageUrl}
                brandColor={design.colors?.[0]}
                locale={locale}
                quoteState={quoteState}
                onQuote={requestQuote}
                mailState={mailState}
                onMail={emailResult}
              />
            ) : canvas === "ai" && result ? (
              result.generatedImageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={result.generatedImageUrl} alt={t.canvas.ai} className="max-h-full w-full max-w-[900px] object-contain shadow-2xl" />
              ) : (
                <div className="flex flex-col items-center gap-3 bg-white px-8 py-10 text-sm text-slate-600 shadow-xl"><Loader2 className="h-6 w-6 animate-spin text-[#034AA6]" />{t.canvas.aiWaiting}</div>
              )
            ) : (
              <LivePreview design={design} description={description} locale={locale} device={device} />
            )}
          </div>

          {/* Share popover */}
          {shareOpen && result?.shareUrl && (
            <div className="absolute right-3 top-14 z-20 w-64 bg-white p-3 shadow-2xl ring-1 ring-slate-200 lg:right-3">
              <div className="mb-2 flex items-center justify-between"><p className="text-sm font-semibold">{t.rail.share}</p><button type="button" onClick={() => setShareOpen(false)} aria-label="Kapat"><X className="h-4 w-4" /></button></div>
              <button type="button" onClick={copyLink} className="mb-2 flex w-full items-center gap-2 border border-slate-200 px-3 py-2 text-sm hover:border-slate-400">{copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}{copied ? t.share.copied : t.share.copy}</button>
              <div className="grid grid-cols-2 gap-2">
                {(["whatsapp", "linkedin", "x", "facebook", "telegram"] as const).map((p) => (
                  <button key={p} type="button" onClick={() => shareTo(p)} className="border border-slate-200 px-2 py-1.5 text-xs hover:border-[#034AA6] hover:text-[#034AA6]">{{ whatsapp: "WhatsApp", linkedin: "LinkedIn", x: "X", facebook: "Facebook", telegram: "Telegram" }[p]}</button>
                ))}
              </div>
            </div>
          )}

          {/* E-mail feedback toast */}
          {mailState.text && (
            <div role="status" className={`absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 px-4 py-2.5 text-sm text-white shadow-xl ${mailState.ok ? "bg-slate-900" : "bg-red-600"}`}>
              {mailState.ok ? <CheckCircle2 className="h-4 w-4 text-green-400" /> : <X className="h-4 w-4" />}
              {mailState.text}
              <button type="button" onClick={() => setMailState((m) => ({ ok: m.ok }))} aria-label="Kapat" className="ml-1 opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}

          {/* Analysis progress */}
          {analyzing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
              <div className="w-full max-w-sm bg-white p-6 shadow-2xl">
                <p className="mb-4 flex items-center gap-2 font-semibold"><Sparkles className="h-5 w-5 text-[#034AA6]" />{t.analyze}…</p>
                <ul className="space-y-2.5">
                  {t.progress.map((p, i) => (
                    <li key={p} className={`flex items-center gap-3 text-sm ${i <= progress ? "text-slate-900" : "text-slate-400"}`}>
                      {i < progress ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : i === progress ? <Loader2 className="h-4 w-4 animate-spin text-[#034AA6]" /> : <span className="h-4 w-4 border border-slate-300" />}
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 h-1 overflow-hidden bg-slate-100"><div className="h-full bg-[#034AA6] transition-all duration-[8000ms] ease-linear" style={{ width: `${((progress + 1) / t.progress.length) * 100}%` }} /></div>
                <p className="mt-3 text-xs text-slate-500">{t.progressNote}</p>
              </div>
            </div>
          )}
        </main>

        {/* Right rail */}
        <nav className="order-3 flex gap-1 overflow-x-auto border-t border-slate-200 bg-white p-2 lg:flex-col lg:border-l lg:border-t-0" aria-label="Tools">
          {railButtons.map((b) => {
            const Icon = b.done ? CheckCircle2 : b.key === "mail" && mailState.busy ? Loader2 : b.key === "quote" && quoteState === "busy" ? Loader2 : b.icon
            const cls = `flex min-w-[76px] shrink-0 flex-col items-center justify-center gap-1 px-1 py-2.5 text-center transition lg:w-full ${b.done ? "text-green-600" : "text-slate-700 hover:bg-slate-100"} disabled:cursor-not-allowed disabled:opacity-30`
            const tip = <span className="text-[10.5px] font-medium leading-tight">{b.label}</span>
            return b.href ? (
              <a key={b.key} href={b.href} target="_blank" rel="noopener noreferrer" download aria-label={b.label} className={cls}><Icon className="h-5 w-5" />{tip}</a>
            ) : (
              <button key={b.key} type="button" onClick={b.onClick} disabled={b.disabled} aria-label={b.label} className={cls}>
                <Icon className={`h-5 w-5 ${Icon === Loader2 ? "animate-spin" : ""}`} />{tip}
              </button>
            )
          })}
        </nav>
      </div>

      {/* ---------- Bottom bar ---------- */}
      <footer className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-slate-200 bg-white px-3 py-3 sm:px-4 lg:static lg:z-30">
        <button type="button" onClick={() => goTo(Math.max(0, step - 1))} disabled={step === 0 || analyzing} className="flex h-12 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:border-slate-500 disabled:opacity-30">
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{t.back}</span>
        </button>

        <div className="min-w-0 flex-1 px-2">
          {result ? (
            <div className="flex gap-5 overflow-x-auto text-sm">
              {[[t.sumComplexity, t.complexity[result.estimatedComplexity] ?? result.estimatedComplexity], [t.sumTime, result.estimatedTimeline], [t.sumBudget, result.estimatedBudget]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="shrink-0">
                  <p className="font-mono text-[10px] font-bold tracking-widest text-slate-500">{String(k).toUpperCase()}</p>
                  <p className="font-semibold text-slate-900">{v}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="hidden items-center gap-2 text-xs text-slate-500 md:flex">
              {design.colors && <span className="flex">{design.colors.map((c) => <span key={c} className="h-4 w-4 ring-1 ring-white" style={{ background: c }} />)}</span>}
              <span className="truncate">{[design.brandName, label(PROJECT_TYPES, design.projectType), label(STYLES, design.style)].filter(Boolean).join(" · ") || t.sumHint}</span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={primary.onClick}
          disabled={primary.disabled}
          className="flex h-12 items-center gap-2 bg-[#034AA6] px-5 font-mono text-sm font-bold tracking-wide text-white transition hover:bg-[#022859] disabled:opacity-60 sm:px-8"
        >
          {(step === 3 && analyzing) || quoteState === "busy" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {primary.text.toUpperCase()}
          {!analyzing && quoteState !== "busy" && <primary.icon className="h-4 w-4" />}
        </button>
      </footer>

      {/* ---------- First-visit intro ---------- */}
      {intro && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/30 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm bg-white p-6 shadow-2xl ring-1 ring-slate-900">
            <p className="text-lg font-bold text-slate-900">{t.intro.title}</p>
            <ul className="mt-4 space-y-4">
              {t.intro.items.map(([h, d], i) => (
                <li key={h} className="flex gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-slate-100">
                    {[<Type key="a" className="h-5 w-5" />, <Palette key="b" className="h-5 w-5" />, <Sparkles key="c" className="h-5 w-5 text-[#034AA6]" />][i]}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{h}</span>
                    <span className="block text-sm text-slate-600">{d}</span>
                  </span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={closeIntro} className="mt-6 h-11 w-full bg-[#034AA6] font-mono text-sm font-bold tracking-wide text-white hover:bg-[#022859]">{t.intro.ok}</button>
          </div>
        </div>
      )}
    </div>
  )
}
